import TermsModal from "@/components/modal/TermsModal";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { useQuoteContext } from "@/contexts/quote-context";
import { copyToClipboard } from "@/utils/copy-clipboard";
import { formatMoneyBr } from "@/utils/format";
import {
  cleanApiUrl,
  ensurePdfExtension,
  openInNewTab,
} from "@/utils/url-helpers";
import * as Sentry from "@sentry/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  FaCopy,
  FaDownload,
  FaExternalLinkAlt,
  FaFileInvoice,
} from "react-icons/fa";
import CouponComponent from "./coupon-component";
import type { PaymentFormValues } from "./payment-types";
import { PersonalInfoDisplay } from "./personal-info-display";

interface BankSlipPaymentProps {
  quotePrice: number;
  setGeneratedBoleto: (value: boolean) => void;
  generatedBoleto: boolean;
  onNextStep: () => void;
}

type PaymentError = Error & {
  rawBackendResponse?: unknown;
  httpStatus?: number;
  needsVerification?: boolean;
};

export default function BankSlipPayment({
  quotePrice,
  setGeneratedBoleto,
  generatedBoleto,
  onNextStep,
}: BankSlipPaymentProps) {
  const form = useFormContext<PaymentFormValues>();
  const {
    payWithBoleto,
    refetchTransaction,
    checkPaymentStatus,
    transactionId,
    transaction,
    cotacao,
    persistedBoletoData,
    saveBoletoData,
  } = useQuoteContext();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isTermsModalOpen, setTermsModalOpen] = useState(false);

  const handleCopyCode = () => {
    const code = persistedBoletoData?.data.digitable_line;
    if (!code || code.trim().length === 0) {
      toast({
        title: "Código não disponível",
        description: "Utilize os botões para visualizar ou baixar o boleto.",
        variant: "destructive",
      });
      return;
    }
    copyToClipboard(code, "Código copiado!");
  };

  const handleGenerateBoleto = async () => {
    // Proteção: se já tem dados persistidos, não gerar nova cobrança
    if (persistedBoletoData) {
      setGeneratedBoleto(true);
      return;
    }

    const isValid = await form.trigger(["termsAccepted"]);

    if (!isValid) return;
    setLoading(true);

    const values = form.getValues();

    try {
      const response = await payWithBoleto(values);
      saveBoletoData(response);
      setGeneratedBoleto(true);
    } catch (err: unknown) {
      const paymentError = err as PaymentError;

      if (paymentError?.needsVerification !== false) {
        toast({
          title: "Verificando pagamento...",
          description: "Aguarde, estamos verificando se o boleto foi gerado.",
        });
        const faturas = await refetchTransaction();
        const boletoFatura = faturas.find(
          (f) =>
            f.por_descricao.toLowerCase().includes("boleto") &&
            f.tts_descricao.toLowerCase() !== "vencido",
        );
        if (boletoFatura) {
          setGeneratedBoleto(true);
          return;
        }
      }

      const description =
        paymentError?.message ||
        "Tente novamente mais tarde ou verifique as informações cadastradas";
      setError(description);
      toast({
        title: "Erro ao pagar com Boleto",
        description,
        variant: "destructive",
      });
      Sentry.withScope((scope) => {
        scope.setTag("payment_method", "boleto");
        scope.setContext("payment_error", {
          userMessage: description,
          cotacaoId: cotacao?.cve_codigo,
          clientEmail: cotacao?.ctc_email,
          clientPhone: cotacao?.ctc_telefone_1,
          backendResponse: paymentError.rawBackendResponse,
          httpStatus: paymentError.httpStatus,
        });
        Sentry.captureException(paymentError);
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const boletoFatura = transaction.find(
      (f) =>
        f.por_descricao.toLowerCase().includes("boleto") &&
        f.tts_descricao.toLowerCase() !== "vencido",
    );

    if (boletoFatura && !persistedBoletoData) {
      setGeneratedBoleto(true);

      const cleanedBankSlipUrl = cleanApiUrl(boletoFatura.bank_slip_url);
      const cleanedSecureUrl = cleanApiUrl(boletoFatura.cre_url_link);
      const cleanedDigitableLine = cleanApiUrl(boletoFatura.digitable_line);
      const cleanedBarcode = cleanApiUrl(boletoFatura.barcode);

      saveBoletoData({
        success: true,
        data: {
          cre_codigo: boletoFatura.cre_codigo,
          invoice_id: boletoFatura.cre_codigo,
          due_date: boletoFatura.cre_data_vencimento,
          digitable_line: cleanedDigitableLine,
          barcode: cleanedBarcode,
          secure_url: cleanedSecureUrl,
          bank_slip_url: cleanedBankSlipUrl,
          bank_slip_pdf_url: ensurePdfExtension(cleanedBankSlipUrl),
        },
      });
    }
  }, [transaction, persistedBoletoData, saveBoletoData, setGeneratedBoleto]);

  useEffect(() => {
    if (!generatedBoleto || !transactionId) return;

    const interval = setInterval(async () => {
      try {
        const status = await checkPaymentStatus(transactionId);
        if (status === "approved") {
          clearInterval(interval);
          onNextStep();
        }
      } catch (error) {
        console.error("Erro ao checar status do pagamento:", error);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [generatedBoleto, transactionId, checkPaymentStatus, onNextStep]);

  return (
    <div className="space-y-6">
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-n-green">Boleto Bancário</h3>

        <div className="space-y-2">
          <p className="font-bold  text-n-text-neutral mb-2">
            Valor total: <span className="">{formatMoneyBr(quotePrice)}</span>
          </p>

          <CouponComponent />
        </div>

        {!generatedBoleto && (
          <>
            <div className="mt-4 border-t pt-4">
              <h4 className="font-bold mb-2 text-n-text-neutral">
                É rápido e prático. Veja como é fácil:
              </h4>
            </div>

            <div className="mt-4">
              <h4 className="font-medium mb-3">Dados do titular:</h4>
              <PersonalInfoDisplay />
            </div>

            <div className="space-y-4 mt-6">
              <FormField
                control={form.control}
                name="termsAccepted"
                render={({ field }) => (
                  <FormItem className="flex items-start justify-start gap-2 mt-6">
                    <FormControl>
                      <Checkbox
                        className="mt-2"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="terms-bank"
                      />
                    </FormControl>
                    <div className="flex-1 flex flex-col">
                      <Label
                        htmlFor="terms-bank"
                        className="text-sm text-n-text mb-2"
                      >
                        Declaro que li e concordo com os{" "}
                        <span
                          className="text-n-primary font-medium cursor-pointer underline hover:text-n-primary/80"
                          onClick={() => setTermsModalOpen(true)}
                        >
                          Termos e Condições
                        </span>{" "}
                        de serviço
                      </Label>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </>
        )}

        {generatedBoleto ? (
          (() => {
            const hasDigitableLine = !!(
              persistedBoletoData?.data.digitable_line &&
              persistedBoletoData.data.digitable_line.trim().length > 0
            );
            const hasPdfUrl = !!persistedBoletoData?.data.bank_slip_pdf_url;
            const hasSecureUrl = !!persistedBoletoData?.data.secure_url;

            return (
              <div className="flex flex-col gap-4 border-t pt-4">
                <p className="text-sm text-n-text-neutral mb-2">
                  Para concluir a sua compra,{" "}
                  {hasDigitableLine
                    ? "realize o pagamento do código de barras abaixo"
                    : "utilize os links abaixo para acessar seu boleto"}
                  .
                </p>

                {hasDigitableLine && (
                  <div className="flex w-full items-center">
                    <Input
                      value={persistedBoletoData?.data.digitable_line || ""}
                      readOnly
                      className="bg-gray-50 text-n-text-neutral text-xs rounded-r-none"
                    />
                    <Button
                      type="button"
                      className="ml-1 rounded-r-lg rounded-l-none h-[48px]"
                      onClick={handleCopyCode}
                    >
                      <FaCopy className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {hasSecureUrl && (
                    <Button
                      type="button"
                      className="rounded-md text-sm"
                      onClick={() =>
                        openInNewTab(
                          persistedBoletoData?.data.secure_url,
                          "Não foi possível abrir o boleto.",
                        )
                      }
                    >
                      Abrir boleto
                      <FaExternalLinkAlt className="ml-2 h-4 w-4" />
                    </Button>
                  )}

                  {hasPdfUrl && (
                    <Button
                      type="button"
                      className="rounded-md text-sm"
                      onClick={() =>
                        openInNewTab(
                          persistedBoletoData?.data.bank_slip_pdf_url,
                          "Não foi possível baixar o PDF.",
                        )
                      }
                    >
                      Baixar PDF do Boleto
                      <FaDownload className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="mt-4">
                  <p className="text-n-text-neutral text-sm mb-2">
                    Pronto!{" "}
                    {hasDigitableLine
                      ? "Você pode copiar o código acima ou "
                      : "Você pode "}
                    usar os botões para visualizar e baixar o boleto. Aprovação
                    do pedido em até 48 horas. Você pode acompanhá-lo pela{" "}
                    <span className="font-bold text-n-primary hover:text-n-primary/80 underline cursor-pointer">
                      área do cliente.
                    </span>
                  </p>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="flex flex-col items-center space-y-4 md:py-6 py-2">
            <div className="bg-n-bg-1 p-6 border rounded-lg w-full flex flex-col items-center">
              <FaFileInvoice className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-sm text-n-text-neutral mb-4 text-center">
                O boleto será gerado após a finalização do pedido e enviado para
                o seu e-mail.
              </p>
              <Button
                type="submit"
                className="flex items-center gap-2"
                onClick={handleGenerateBoleto}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Gerando
                  </>
                ) : (
                  <>
                    <FaDownload className="h-4 w-4" />
                    Gerar Boleto
                  </>
                )}
              </Button>
              {error && (
                <p className="text-sm text-red-500 text-center mt-2">{error}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
